import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    Typography,
    Grid,
    Chip,
    Fab,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import TimerIcon from '@mui/icons-material/Timer';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TranslateIcon from '@mui/icons-material/Translate';
import BarChartIcon from '@mui/icons-material/BarChart';
import StyleIcon from '@mui/icons-material/Style';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TuneIcon from '@mui/icons-material/Tune';
import { useHasActiveAccess, SubscriberCtaContent } from '../components/AdaptiveCta';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { keyframes } from '@mui/system';

const cyan = '#00BFFF';
const green = '#8DC63F';
const cardBg = '#141E30';
const borderColor = 'rgba(0, 191, 255, 0.12)';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const GlassCard = ({ children, sx = {}, ...props }) => (
    <Box
        sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha(cardBg, 0.7) : 'background.paper',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            backdropFilter: 'blur(12px)',
            p: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
                borderColor: alpha(cyan, 0.3),
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 40px ${alpha('#000', 0.4)}`,
            },
            ...sx,
        }}
        {...props}
    >
        {children}
    </Box>
);

const ScrollToTopButton = () => {
    const [visible, setVisible] = React.useState(false);
    const [fade, setFade] = React.useState(false);

    React.useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 200) {
                setVisible(true);
            } else {
                setVisible(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleClick = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setFade(true);
        setTimeout(() => {
            setVisible(false);
            setFade(false);
        }, 500);
    };

    if (!visible) return null;

    return (
        <Box onClick={handleClick} sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000, opacity: fade ? 0 : 1, transition: 'opacity 0.5s ease-out' }}>
            <Fab color="primary" aria-label="scroll back to top"><KeyboardArrowUpIcon /></Fab>
        </Box>
    );
};

const Features = () => {
    const navigate = useNavigate();
    const hasActiveAccess = useHasActiveAccess();

    const coreFeatures = [
        {
            icon: <SpeedIcon sx={{ fontSize: 36 }} />,
            title: 'Adaptive Speed Reader',
            desc: 'A full-screen reading interface with adjustable WPM, dynamic text highlighting, and real-time speed tracking. Your pace adjusts as you improve.',
            color: cyan,
            tag: 'Core',
        },
        {
            icon: <SchoolIcon sx={{ fontSize: 36 }} />,
            title: 'AI-Generated Articles',
            desc: 'Get fresh practice material on any topic, generated by AI and matched to your reading level. 14 difficulty levels from Grade 1 to Native.',
            color: green,
            tag: 'AI',
        },
        {
            icon: <PsychologyIcon sx={{ fontSize: 36 }} />,
            title: 'Comprehension Quizzes',
            desc: 'Every article comes with auto-generated quizzes to verify your retention. Track your comprehension rate across sessions.',
            color: cyan,
            tag: 'Core',
        },
        {
            icon: <TranslateIcon sx={{ fontSize: 36 }} />,
            title: '16 Languages',
            desc: 'Read and train in English, Spanish, French, German, Japanese, Korean, Chinese, Arabic, Hindi, and 7 more languages.',
            color: green,
            tag: 'Languages',
        },
        {
            icon: <StyleIcon sx={{ fontSize: 36 }} />,
            title: 'Vocabulary Bank',
            desc: 'Save words as you read. Each word gets phonetic pronunciation, definition, and source tracking. Review with spaced-repetition flashcards.',
            color: cyan,
            tag: 'Learning',
        },
        {
            icon: <RecordVoiceOverIcon sx={{ fontSize: 36 }} />,
            title: 'Pronunciation Guide',
            desc: 'Difficult words from each article are flagged with phonetic guides. Non-Latin scripts include romanization for easier learning.',
            color: green,
            tag: 'Languages',
        },
    ];

    const progressFeatures = [
        {
            icon: <BarChartIcon sx={{ fontSize: 36 }} />,
            title: 'Progress Dashboard',
            desc: 'XP bar, daily goals, weekly trend charts, and login streaks. See your improvement at a glance every time you open the app.',
            color: cyan,
        },
        {
            icon: <TuneIcon sx={{ fontSize: 36 }} />,
            title: '14 Reading Levels',
            desc: 'From Grade 1 (150-300 words, simple vocabulary) to Native (600-1000 words, complex topics). Articles scale to your ability.',
            color: green,
        },
        {
            icon: <TrendingUpIcon sx={{ fontSize: 36 }} />,
            title: 'Per-Language Stats',
            desc: 'Track articles read, words read, quizzes passed, and grade progression independently for each of the 16 languages.',
            color: cyan,
        },
        {
            icon: <WhatshotIcon sx={{ fontSize: 36 }} />,
            title: 'Streak System',
            desc: 'Daily login streaks with XP multipliers. The longer your streak, the faster you level up. Miss a day and it resets.',
            color: green,
        },
    ];

    const socialFeatures = [
        {
            icon: <EmojiEventsIcon sx={{ fontSize: 36 }} />,
            title: '103 Badges Across 17 Categories',
            desc: '5 rarity tiers from Bronze to Diamond. Categories include reading, speed, comprehension, streaks, vocabulary, language, FLEX social, onboarding, and more.',
            color: cyan,
        },
        {
            icon: <TimerIcon sx={{ fontSize: 36 }} />,
            title: 'FLEX Cards',
            desc: 'Generate shareable achievement cards after every reading session. Download, share to social media, or send a challenge link to a friend.',
            color: '#FFD700',
        },
        {
            icon: <LeaderboardIcon sx={{ fontSize: 36 }} />,
            title: 'Reading Challenges',
            desc: 'Challenge anyone to a head-to-head reading duel. Guests can participate without an account. Results merge on registration with full attribution.',
            color: '#FF9800',
        },
        {
            icon: <StyleIcon sx={{ fontSize: 36 }} />,
            title: 'Public FLEX Profiles',
            desc: 'Opt-in public profile at /flex/yourhandle. Control which stats are visible. Share your card gallery, challenge record, and reading achievements.',
            color: cyan,
        },
        {
            icon: <LeaderboardIcon sx={{ fontSize: 36 }} />,
            title: 'Enhanced Leaderboards',
            desc: 'Effective WPM ranking with 80% comprehension qualification. Sort by speed, comprehension, streaks, most-improved, volume, and challenge wins.',
            color: green,
        },
        {
            icon: <MonetizationOnIcon sx={{ fontSize: 36 }} />,
            title: 'FLEX Feed & Group Challenges',
            desc: 'Share progress to the community feed with reactions. Create private reading groups for workplaces, book clubs, and classrooms with group leaderboards.',
            color: cyan,
        },
        {
            icon: <MonetizationOnIcon sx={{ fontSize: 36 }} />,
            title: 'Affiliate Program',
            desc: 'Earn $3.00/month for direct referrals and $1.00/month for second-level referrals. Payouts via PayPal, bank transfer, or store credit with a 5% bonus.',
            color: green,
        },
    ];

    return (
        <Box sx={{ overflow: 'hidden' }}>
            {/* Hero */}
            <Box sx={{
                py: { xs: 8, md: 12 },
                textAlign: 'center',
                position: 'relative',
                '&::before': {
                    content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    background: `radial-gradient(ellipse at 50% 0%, ${alpha(cyan, 0.06)} 0%, transparent 60%)`,
                    pointerEvents: 'none',
                },
            }}>
                <Container maxWidth="md">
                    <Box sx={{ animation: `${fadeIn} 0.8s ease-out` }}>
                        <Typography variant="h2" component="h1" sx={{ fontWeight: 800, mb: 2, color: 'text.primary', fontSize: { xs: '2.2rem', md: '3rem' } }}>
                            Built for <Box component="span" sx={{ color: cyan }}>Serious Readers</Box>
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, maxWidth: 600, mx: 'auto', mb: 4, lineHeight: 1.6 }}>
                            Every feature is designed to help you read faster, retain more, and track your growth with precision.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* Core Features */}
            <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
                <Typography variant="h4" sx={{ mb: 1, color: 'text.primary', fontWeight: 700 }}>
                    Core Training Tools
                </Typography>
                <Typography variant="body1" sx={{ mb: 5, color: 'text.secondary' }}>
                    The engine behind your speed reading improvement.
                </Typography>
                <Grid container spacing={3}>
                    {coreFeatures.map((f, i) => (
                        <Grid item xs={12} sm={6} key={i}>
                            <GlassCard sx={{ height: '100%', display: 'flex', gap: 2 }}>
                                <Box sx={{ width: 52, height: 52, minWidth: 52, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: alpha(f.color, 0.12), color: f.color }}>
                                    {f.icon}
                                </Box>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>{f.title}</Typography>
                                        <Chip label={f.tag} size="small" sx={{ height: 20, fontSize: '0.65rem', backgroundColor: alpha(f.color, 0.12), color: f.color }} />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{f.desc}</Typography>
                                </Box>
                            </GlassCard>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Progress & Analytics */}
            <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: alpha('#000', 0.2) }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" sx={{ mb: 1, color: 'text.primary', fontWeight: 700 }}>
                        Progress & Analytics
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 5, color: 'text.secondary' }}>
                        Data-driven insights into every aspect of your reading performance.
                    </Typography>
                    <Grid container spacing={3}>
                        {progressFeatures.map((f, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <GlassCard sx={{ height: '100%', textAlign: 'center' }}>
                                    <Box sx={{ color: f.color, mb: 1.5 }}>{f.icon}</Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>{f.title}</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{f.desc}</Typography>
                                </GlassCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Social & Gamification */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                <Typography variant="h4" sx={{ mb: 1, color: 'text.primary', fontWeight: 700 }}>
                    Gamification & Community
                </Typography>
                <Typography variant="body1" sx={{ mb: 5, color: 'text.secondary' }}>
                    Stay motivated with achievements, leaderboards, and real rewards.
                </Typography>
                <Grid container spacing={3}>
                    {socialFeatures.map((f, i) => (
                        <Grid item xs={12} sm={6} key={i}>
                            <GlassCard sx={{ height: '100%', display: 'flex', gap: 2 }}>
                                <Box sx={{ width: 52, height: 52, minWidth: 52, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: alpha(f.color, 0.12), color: f.color }}>
                                    {f.icon}
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>{f.title}</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{f.desc}</Typography>
                                </Box>
                            </GlassCard>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* CTA */}
            <Box sx={{
                py: { xs: 6, md: 10 }, textAlign: 'center',
                borderTop: `1px solid ${borderColor}`,
            }}>
                <Container maxWidth="sm">
                    {hasActiveAccess ? (
                        <SubscriberCtaContent
                            title="Ready to see it in action?"
                            description="Share your affiliate link and earn $3.00/month when friends try these features for themselves."
                        />
                    ) : (
                        <>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
                                Ready to see it in action?
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
                                Try every feature for $1. Full access for 3 days.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button variant="contained" color="secondary" size="large" onClick={() => navigate('/register')} sx={{ py: 1.5, px: 5 }}>
                                    Start Your Trial
                                </Button>
                                <Button variant="outlined" color="primary" size="large" onClick={() => navigate('/subscription')} sx={{ py: 1.5, px: 5 }}>
                                    View Pricing
                                </Button>
                            </Box>
                        </>
                    )}
                </Container>
            </Box>

            <ScrollToTopButton />
        </Box>
    );
};

export default Features;
